import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { addDays, format } from "date-fns";
import type { ContaPagarResumoDto, ContaReceberResumoDto } from "../types";

interface CashFlowProjectionProps {
  contasPagar: ContaPagarResumoDto[];
  contasReceber: ContaReceberResumoDto[];
  saldoAtual: number;
}

export const CashFlowProjection = ({
  contasPagar,
  contasReceber,
  saldoAtual,
}: CashFlowProjectionProps) => {
  const projectionData = useMemo(() => {
    const today = new Date();
    const projection = [];
    let currentBalance = saldoAtual;

    // Create a map of dates with transactions
    const dateMap = new Map<string, { entradas: number; saidas: number }>();

    // Add pending receivables (pendente ou atrasado = ainda não pago)
    contasReceber
      .filter(c => !c.isPago)
      .forEach(conta => {
        const dateKey = format(new Date(conta.dtVencimento), "yyyy-MM-dd");
        const existing = dateMap.get(dateKey) || { entradas: 0, saidas: 0 };
        existing.entradas += conta.valorSaldo;
        dateMap.set(dateKey, existing);
      });

    // Add pending payables (pendente ou atrasado = ainda não pago)
    contasPagar
      .filter(c => !c.isPago)
      .forEach(conta => {
        const dateKey = format(new Date(conta.dtVencimento), "yyyy-MM-dd");
        const existing = dateMap.get(dateKey) || { entradas: 0, saidas: 0 };
        existing.saidas += conta.valorSaldo;
        dateMap.set(dateKey, existing);
      });

    // Generate projection for next 30 days
    for (let i = 0; i <= 30; i++) {
      const date = addDays(today, i);
      const dateKey = format(date, "yyyy-MM-dd");
      const dateLabel = format(date, "dd/MM");
      
      const transactions = dateMap.get(dateKey) || { entradas: 0, saidas: 0 };
      currentBalance += transactions.entradas - transactions.saidas;

      projection.push({
        date: dateLabel,
        saldo: currentBalance,
        entradas: transactions.entradas,
        saidas: transactions.saidas,
      });
    }

    return projection;
  }, [contasPagar, contasReceber, saldoAtual]);

  const minBalance = Math.min(...projectionData.map(d => d.saldo));
  const hasNegativeProjection = minBalance < 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Projeção de Fluxo de Caixa</CardTitle>
        <CardDescription>
          Projeção baseada em contas pendentes para os próximos 30 dias
          {hasNegativeProjection && (
            <span className="text-destructive font-medium"> - Atenção: Saldo negativo previsto!</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={projectionData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
              interval={4}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
              tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number) => [
                value.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }),
              ]}
            />
            <Legend />
            <ReferenceLine 
              y={0} 
              stroke="hsl(var(--destructive))" 
              strokeDasharray="3 3"
              label={{ value: "Limite Zero", position: "insideTopRight" }}
            />
            <Line
              type="monotone"
              dataKey="saldo"
              stroke="hsl(var(--chart-3))"
              strokeWidth={3}
              name="Saldo Projetado"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">Saldo Atual</p>
            <p className={`text-lg font-bold ${saldoAtual >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {saldoAtual.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Saldo Projetado (30 dias)</p>
            <p className={`text-lg font-bold ${projectionData[30].saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {projectionData[30].saldo.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Menor Saldo Previsto</p>
            <p className={`text-lg font-bold ${minBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {minBalance.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
